import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Tema da plataforma (escuro / claro / sistema). **Padrão = escuro** (sem escolha guardada). A escolha fica em
 * `localStorage["pro-roller.theme"]` (light | dark | system); o tema efetivo vai em `<html data-theme="light|dark">` (os
 * tokens de `tokens.css` trocam por esse atributo). `index.html` aplica o atributo antes do React montar, para não piscar.
 */
export type TemaEscolha = "light" | "dark" | "system";
export type TemaEfetivo = "light" | "dark";

export const THEME_KEY = "pro-roller.theme";
const ouvintes = new Set<() => void>();

export function lerEscolha(): TemaEscolha {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "dark";
  } catch {
    return "dark";
  }
}

const doSistema = (): TemaEfetivo => (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light");
export const temaEfetivo = (escolha: TemaEscolha = lerEscolha()): TemaEfetivo => (escolha === "system" ? doSistema() : escolha);

export function aplicarTema(escolha: TemaEscolha = lerEscolha()) {
  const efetivo = temaEfetivo(escolha);
  document.documentElement.setAttribute("data-theme", efetivo);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", efetivo === "dark" ? "#0B1220" : "#FAFBFC");
}

export function definirTema(escolha: TemaEscolha) {
  try {
    localStorage.setItem(THEME_KEY, escolha);
  } catch {
    /* modo privado */
  }
  aplicarTema(escolha);
  ouvintes.forEach((f) => f());
}

const subscribe = (f: () => void) => {
  ouvintes.add(f);
  const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
  const onMq = () => { if (lerEscolha() === "system") aplicarTema("system"); f(); };
  mq?.addEventListener?.("change", onMq);
  return () => { ouvintes.delete(f); mq?.removeEventListener?.("change", onMq); };
};

export function useTheme() {
  const escolha = useSyncExternalStore(subscribe, lerEscolha, () => "dark" as TemaEscolha);
  const efetivo = useSyncExternalStore(subscribe, () => temaEfetivo(), () => "dark" as TemaEfetivo);
  useEffect(() => { aplicarTema(); }, []);
  const definir = useCallback((e: TemaEscolha) => definirTema(e), []);
  return { escolha, efetivo, definir };
}
